import { useState } from 'react';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useT } from '../lib/i18n';

// Small (?) icon that opens a single-pane explainer modal. Used next to
// section eyebrows on the dashboard where a derived metric isn't worth
// a full per-value explainer (like the Earnings Outlook block has) but
// still has enough hidden math that a barber benefits from a one-tap
// definition.
//
// Wraps the same ModalShell pattern as the rest of the app's modals,
// styled minimally — title + body + close.

export function HelpHint({
  titleKey,
  bodyKey,
  ariaKey,
}: {
  titleKey: string;
  bodyKey: string;
  /** i18n key for the screen-reader-only label on the (?) button.
   *  Should describe what the modal explains (e.g. "Explain how the
   *  prior-period delta is calculated"). */
  ariaKey: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-ink-500/70 hover:text-navy-900 dark:hover:text-gold-500 hover:bg-bg-alt transition-colors"
        aria-label={t(ariaKey)}
      >
        <QuestionIcon className="w-3 h-3" />
      </button>
      {open && (
        <ModalShell open onClose={() => setOpen(false)} size="sm" ariaLabel={t(titleKey)}>
          <ModalCloseButton onClose={() => setOpen(false)} />
          <div className="p-5 md:p-6 space-y-3">
            <header className="pr-10">
              <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
                {t('helpHint.eyebrow')}
              </div>
              <h2 className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight mt-0.5">
                {t(titleKey)}
              </h2>
            </header>
            <p className="text-[14px] text-ink-700 leading-relaxed">{t(bodyKey)}</p>
          </div>
        </ModalShell>
      )}
    </>
  );
}

function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
